/**
 * What the student set under "Anpassa" (pedagogy spec §6.3.4), and the persistent skip list.
 * Both live in `localStorage` (guarded: private mode and blocked storage fall back to the
 * defaults, and a failed write is simply not remembered). The server has no settings column
 * and no skip endpoint: the plan takes `retests`, `newTopic` and `skip` as query parameters
 * (spec §6.3.2).
 *
 * - `mf.today.settings`: cards on/off and their cap, the number of re-tests, the new topic on/off,
 *   and a topic override that lasts one day.
 * - `mf.today.skipTopics`: topic ids the automatic new-topic pick passes over, until the student
 *   chooses one by hand.
 */
import { DEFAULT_TIMEZONE, dayKey } from "@shared/time";
import { todayDefaultsFor, type UserPrefs } from "@shared/userPrefs";

export const SETTINGS_KEY = "mf.today.settings";
export const SKIP_KEY = "mf.today.skipTopics";

/** What the server accepts for `retests` (0–5) and the default it applies. */
export const MAX_RETESTS = 5;
export const DEFAULT_RETESTS = 3;
/** The cap choices for the warm-up cards. */
export const CARD_LIMITS = [10, 20, 30] as const;
export type CardLimit = (typeof CARD_LIMITS)[number];
export const DEFAULT_CARD_LIMIT: CardLimit = 20;
/** The new topic's step holds this many problems (the server's default `newCount`). */
export const NEW_ITEMS = 3;

export interface TodaySettings {
  cards: boolean;
  cardLimit: CardLimit;
  retests: number;
  includeNew: boolean;
  /** A topic picked by hand for one day; the automatic pick (course order) otherwise. */
  newTopic?: { id: string; dayKey: string };
}

export const DEFAULT_SETTINGS: TodaySettings = {
  cards: true,
  cardLimit: DEFAULT_CARD_LIMIT,
  retests: DEFAULT_RETESTS,
  includeNew: true,
};

/**
 * The defaults for a student's "Kom igång" answers (slice C): the card cap follows the time per
 * day. What the student set under "Anpassa" on this device still wins (`mf.today.settings`).
 */
export function defaultSettingsFor(prefs: UserPrefs | null | undefined): TodaySettings {
  return { ...DEFAULT_SETTINGS, cardLimit: todayDefaultsFor(prefs).cardLimit };
}

/** The local day in the app timezone, the same one the server plans for. */
export function todayKey(now: number = Date.now()): string {
  return dayKey(now, DEFAULT_TIMEZONE);
}

const TOPIC_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Settings from a stored value; anything unreadable falls back to the default for that field. */
export function parseSettings(
  raw: unknown,
  today: string,
  defaults: TodaySettings = DEFAULT_SETTINGS,
): TodaySettings {
  const stored = record(raw);
  if (!stored) return { ...defaults };
  const limit = CARD_LIMITS.find((n) => n === stored.cardLimit) ?? defaults.cardLimit;
  const retests =
    typeof stored.retests === "number" &&
    Number.isInteger(stored.retests) &&
    stored.retests >= 0 &&
    stored.retests <= MAX_RETESTS
      ? stored.retests
      : DEFAULT_RETESTS;
  const override = record(stored.newTopic);
  const newTopic =
    override &&
    typeof override.id === "string" &&
    TOPIC_ID.test(override.id) &&
    override.dayKey === today
      ? { id: override.id, dayKey: today }
      : undefined;
  return {
    cards: stored.cards !== false,
    cardLimit: limit,
    retests,
    includeNew: stored.includeNew !== false,
    ...(newTopic ? { newTopic } : {}),
  };
}

/** The settings with the one-day topic override taken off. */
export function withoutOverride(settings: TodaySettings): TodaySettings {
  return {
    cards: settings.cards,
    cardLimit: settings.cardLimit,
    retests: settings.retests,
    includeNew: settings.includeNew,
  };
}

export function loadSettings(
  today: string = todayKey(),
  defaults: TodaySettings = DEFAULT_SETTINGS,
): TodaySettings {
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    return parseSettings(raw ? (JSON.parse(raw) as unknown) : null, today, defaults);
  } catch {
    return { ...defaults };
  }
}

export function saveSettings(settings: TodaySettings): void {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* storage full or blocked: the choice holds for this visit only */
  }
}

/** The skip list: well-formed topic ids, each once. */
export function loadSkipTopics(): string[] {
  try {
    const raw = window.localStorage.getItem(SKIP_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const ids = parsed.filter((id): id is string => typeof id === "string" && TOPIC_ID.test(id));
    return [...new Set(ids)];
  } catch {
    return [];
  }
}

export function saveSkipTopics(ids: readonly string[]): void {
  try {
    window.localStorage.setItem(SKIP_KEY, JSON.stringify([...new Set(ids)]));
  } catch {
    /* as above */
  }
}

/** Forgets the settings and the skip list: they are one student's choices (sign-out). */
export function clearTodaySettings(): void {
  try {
    window.localStorage.removeItem(SETTINGS_KEY);
    window.localStorage.removeItem(SKIP_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The query string of `GET /api/practice/today` for these settings. `include=questions` is for
 * the runner, which starts the session from the response; Home only needs the counts.
 */
export function planQueryString(
  settings: TodaySettings,
  skip: readonly string[],
  includeQuestions: boolean,
): string {
  const search = new URLSearchParams();
  search.set("retests", String(settings.retests));
  if (settings.newTopic) search.set("newTopic", settings.newTopic.id);
  if (skip.length > 0) search.set("skip", skip.join(","));
  if (includeQuestions) search.set("include", "questions");
  return search.toString();
}

/** The part of the plan's query key that changes with the settings (spec §6.3.4). */
export function settingsHash(settings: TodaySettings, skip: readonly string[]): string {
  return `${settings.retests}|${settings.newTopic?.id ?? ""}|${skip.join(",")}`;
}
